import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import { MarketingTile, MarketingTileSet } from './MarketingTypes';
import { MarketingService } from './MarketingService';

interface MarketingState {
  tiles: MarketingTile[];
  draftTiles: MarketingTile[];
  version: number;
  updatedAt?: string;
  updatedBy?: string;
  loading: boolean;
  saving: boolean;
  error?: string;
  dirty: boolean;
  load: () => Promise<void>;
  resetDraft: () => void;
  addTile: () => void;
  updateTile: (id: number, patch: Partial<MarketingTile>) => void;
  removeTile: (id: number) => void;
  save: () => Promise<void>;
}

const MarketingContext = createContext<MarketingState | undefined>(undefined);

const normalizeTiles = (tiles: MarketingTile[]): string => {
  const ordered = [...tiles].sort((a, b) => a.order - b.order || a.id - b.id);
  return JSON.stringify(
    ordered.map((tile) => ({
      ...tile,
      title: tile.title ?? '',
      content: tile.content ?? '',
      link: tile.link ?? ''
    }))
  );
};

const applyServerSet = (set: MarketingTileSet): MarketingTile[] =>
  set.tiles.map((tile) => ({
    ...tile,
    title: tile.title ?? '',
    content: tile.content ?? '',
    link: tile.link ?? ''
  }));

export const MarketingProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [tiles, setTiles] = useState<MarketingTile[]>([]);
  const [draftTiles, setDraftTiles] = useState<MarketingTile[]>([]);
  const [version, setVersion] = useState(0);
  const [updatedAt, setUpdatedAt] = useState<string | undefined>();
  const [updatedBy, setUpdatedBy] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const loadedOnceRef = useRef(false);
  const loadingRef = useRef(false);

  const dirty = useMemo(() => normalizeTiles(tiles) !== normalizeTiles(draftTiles), [tiles, draftTiles]);

  const load = useCallback(async (): Promise<void> => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(undefined);

    try {
      const set = await MarketingService.loadTiles();
      const normalized = applyServerSet(set);
      setTiles(normalized);
      setDraftTiles(normalized);
      setVersion(set.version);
      setUpdatedAt(set.updatedAt);
      setUpdatedBy(set.updatedBy);
      loadedOnceRef.current = true;
    } catch (err) {
      setError('Unable to load marketing tiles right now.');
      if (!loadedOnceRef.current) {
        setTiles([]);
        setDraftTiles([]);
      }
      console.error(err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  const resetDraft = useCallback((): void => {
    setDraftTiles(tiles);
  }, [tiles]);

  const addTile = useCallback((): void => {
    const nextId = draftTiles.length > 0 ? Math.max(...draftTiles.map((tile) => tile.id)) + 1 : 1;
    const nextOrder = draftTiles.length > 0 ? Math.max(...draftTiles.map((tile) => tile.order)) + 1 : 1;
    setDraftTiles((current) => [
      ...current,
      {
        id: nextId,
        title: '',
        content: '',
        link: '',
        color: '#0f4c81',
        surfaces: { public: true, portal: true },
        order: nextOrder,
        updatedAt: new Date().toISOString(),
        version
      }
    ]);
  }, [draftTiles, version]);

  const updateTile = useCallback((id: number, patch: Partial<MarketingTile>): void => {
    setDraftTiles((current) =>
      current.map((tile) => (tile.id === id ? { ...tile, ...patch } : tile))
    );
  }, []);

  const removeTile = useCallback((id: number): void => {
    setDraftTiles((current) => current.filter((tile) => tile.id !== id));
  }, []);

  const save = useCallback(async (): Promise<void> => {
    if (saving) return;
    setSaving(true);
    setError(undefined);

    try {
      const payload = { tiles: draftTiles, expectedVersion: version };
      const set = await MarketingService.saveTiles(payload);
      const normalized = applyServerSet(set);
      setTiles(normalized);
      setDraftTiles(normalized);
      setVersion(set.version);
      setUpdatedAt(set.updatedAt);
      setUpdatedBy(set.updatedBy);
    } catch (err) {
      setError((err as Error).message || 'Unable to save marketing tiles.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }, [draftTiles, saving, version]);

  const value: MarketingState = {
    tiles,
    draftTiles,
    version,
    updatedAt,
    updatedBy,
    loading,
    saving,
    error,
    dirty,
    load,
    resetDraft,
    addTile,
    updateTile,
    removeTile,
    save
  };

  return <MarketingContext.Provider value={value}>{children}</MarketingContext.Provider>;
};

export const useMarketing = (): MarketingState => {
  const context = useContext(MarketingContext);
  if (!context) {
    throw new Error('MarketingContext is not available');
  }
  return context;
};
